import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { Headline, Loading, Refusal, Tooltip } from "@lopie/kernel-ui";

import { SANDBOX_APP } from "./sandbox.js";

/**
 * What is this costing, per app.
 *
 * ## Two kinds of money, and they must not be added carelessly
 *
 * **Cloud** is what AWS charged for the resources an instance runs on. It is a
 * bill: the money has left an account and the figure is not an opinion.
 *
 * **Agents** is what the LLM work cost, computed from each session's reported
 * usage at list prices. `runs.spend_of` carries that sentence as `basis`
 * explicitly *"so that a screen cannot render the number without the
 * caveat"* — an org on committed-use pricing paid less than this says, and
 * nothing here knows by how much.
 *
 * So the two sit in their own columns, the combined figure carries the
 * caveat wherever it appears, and neither is ever rendered as an invoice.
 * A total that silently mixed a bill with an estimate would be the most
 * plausible wrong number this console could produce.
 *
 * ## No "what this does not prove" block, and that is the rule not a lapse
 *
 * `CLAUDE.md` says the qualification belongs on the figure it qualifies, and
 * that reading the rule as *a block at the foot of every screen* produces
 * "eight identical controls nobody opens twice". This screen had one. It read
 * as doubt about whether the numbers were any good rather than as precision
 * about what they are, which is the opposite of what a caveat is for.
 *
 * So each qualification sits on its own figure: the two kinds of money are on
 * the headline's `basis`, the projection's assumption is under the projection,
 * and Cost Explorer's day of lag is next to the date it applies to. Anything
 * that could not be attached to a number was not a caveat, it was a gap, and
 * a gap gets fixed or reported rather than printed.
 *
 * ## Both columns are the same month
 *
 * Cloud is the calendar month. Agent spend is the same month, summed from
 * the per-run rows the kernel writes with a timestamp on each one — not the
 * accumulated `#SPEND` total, which has no dates in it and is every run an
 * agent has ever made.
 *
 * The first version put those side by side and captioned the difference.
 * That is not a caveat, it is two numbers that cannot be compared sitting in
 * a table whose whole purpose is comparing them.
 *
 * **What that costs, and it is temporary.** Per-run rows began partway
 * through this month, so runs before that are in an agent's lifetime total
 * and in no month at all. That money is named on its own line rather than
 * dropped — it was really spent — and the line goes once those runs are
 * older than the month being shown.
 *
 * ## What it does not show
 *
 * Yesterday. Cost Explorer runs about a day behind and every record says so
 * in `as_of`, which is printed rather than smoothed over — a page that showed
 * a total to the minute would be claiming a freshness the source does not
 * have.
 */

const money = (usd) =>
  Number(usd || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

/** Minor units from the kernel, which stores cents and says so. */
const fromMinor = (n) => (Number(n) || 0) / 100;

/**
 * Which AWS services are the platform, and which belong to an app.
 *
 * ## Why the screen classifies at all
 *
 * Spend that carries no tag is spend Cost Explorer could not group, and the
 * honest label for that is "we could not tell". But it is not the label these
 * rows deserve: the services in them are the search domain, the key store, the
 * secret store and DNS — the things Kernel itself needs to be running before
 * any app exists. Calling that "unattributed" describes the tagging and not
 * the money.
 *
 * So the name comes from what it was spent on rather than from what reached
 * it. `Kernel platform` is a claim the service mix supports, and the only way
 * to make it a claim rather than a guess is to be able to say which services
 * would contradict it.
 *
 * ## What would contradict it
 *
 * A Lambda, an API Gateway, a DynamoDB table or an app's own bucket. The
 * composition makes one set per app, so their cost is one app's and not the
 * platform's, and it gets its own row rather than being folded into the
 * platform figure — folding it in would be this screen inventing an answer it
 * does not have.
 *
 * **Which app is a separate question and the answer is not here yet.** The
 * resources do carry their `Instance` tag: `get-resources --tag-filters
 * Key=Instance` lists them. Cost Explorer cannot group by a tag until that tag
 * is activated in the account that pays the bill, which is one command nobody
 * has run. So the row says whose kind of cost it is and stops, rather than
 * claiming a resource is missing a tag it is wearing.
 *
 * ## The list is the platform's, not AWS's
 *
 * A service is here because *this* system uses it org-wide, not because it is
 * infrastructural in general. A second kernel that ran its search per app
 * would need this edited, and the edit is the right amount of work for a
 * change that moves a line on somebody's bill.
 */
const PLATFORM_SERVICES = [
  "Amazon OpenSearch Service",   // the org's search tier
  "AWS Key Management Service",  // keys, shared
  "AWS Secrets Manager",         // the org's secrets
  "AWS Systems Manager",         // parameters and session state
  "Amazon Route 53",             // one zone, every app's subdomain
  "AmazonCloudWatch",            // the control plane's own logs and alarms
  "AWS Cost Explorer",           // reading the bill costs money
  "Amazon Simple Notification Service",
  "Amazon Simple Queue Service",
];

const isPlatform = (service) => PLATFORM_SERVICES.includes(service);

const BASIS_NOTE = {
  ce_forecast: "Cost Explorer's own forecast, added to month-to-date.",
  run_rate_7d:
    "Month-to-date plus the mean of the complete days observed. A run-rate is "
    + "not a forecast: it assumes the rest of the month looks like the last "
    + "few days, and it has no opinion about anything that has not happened "
    + "yet.",
  actual_complete: "The month is over. Nothing is projected.",
  none: "Not enough history to project from.",
};

/** One line, so the same sentence is not written three times. */
function AppRow({ row, widest }) {
  const total = row.cloud + row.agents;
  const share = widest ? Math.min(1, total / widest) : 0;
  return (
    <TableRow>
      <TableCell sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ fontWeight: row.platform || row.untraced || row.sandbox ? 400 : 600 }}>
            {row.label}
          </Typography>
          {row.sandbox ? (
            <Tooltip title="What the sandboxes cost to run, added together. A sandbox is a test of a request that has not been approved, so it is not an installed app and no instance or app is named. It is real money in this account, which is why it is here.">
              <Chip size="small" variant="outlined" label="not an install" />
            </Tooltip>
          ) : null}
          {row.platform ? (
            <Tooltip title="What Kernel needs to be running before any app exists: search, keys, secrets and DNS. One set for the whole org, so it is not divided between the apps — a share worked out here would be an invention that looked exactly like a measurement.">
              {/* Was `platform`, beside a row now labelled "Shared services
                  here" — which put the ambiguous word straight back on the
                  screen next to the thing it does not mean. */}
              <Chip size="small" variant="outlined" label="shared" />
            </Tooltip>
          ) : null}
          {row.untraced ? (
            <Tooltip title="A per-app service — an app bucket, a function, a table — so this is one app's cost rather than the platform's. Which app cannot be said yet: the resources carry their Instance tag, and Cost Explorer cannot group by a tag until it is activated in the account that pays the bill.">
              <Chip size="small" variant="outlined" label="one app's" />
            </Tooltip>
          ) : null}
          {row.agentNames.length ? (
            <Typography variant="caption" color="text.secondary">
              {row.agentNames.join(", ")}
            </Typography>
          ) : null}
        </Stack>
        {/* The bar is under the name and not its own column: it compares rows
            and is not a value anybody reads off. */}
        <Box
          aria-hidden
          sx={{
            mt: 0.75,
            height: 3,
            width: `${Math.max(share * 100, total ? 1.5 : 0)}%`,
            bgcolor: row.untraced ? "kernel.attention" : "primary.main",
            opacity: row.untraced ? 0.9 : 0.45,
            borderRadius: 2,
          }}
        />
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {row.cloud ? money(row.cloud) : <Typography variant="body2" color="text.secondary">—</Typography>}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {row.agents ? money(row.agents) : <Typography variant="body2" color="text.secondary">—</Typography>}
        {/* Spent by this app's agents, with nothing saying when. Shown on the
            app's own row so the app stays in the table: summing these at the
            foot of the page made two apps that cost money vanish from it. */}
        {row.undatedAgents ? (
          <Tooltip title="Spent before the kernel began recording a row per run, so it belongs to this app but to no month. Not added to the figures beside it, which are this month.">
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {money(row.undatedAgents)} undated
            </Typography>
          </Tooltip>
        ) : null}
      </TableCell>
      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", fontWeight: 600 }}>
        {money(total)}
      </TableCell>
    </TableRow>
  );
}

export default function Costs({ get, onEffective }) {
  const [spend, setSpend] = useState(null);
  const [agents, setAgents] = useState([]);
  const [failure, setFailure] = useState(null);

  const load = useCallback(async () => {
    try {
      // The cloud half is the one this screen cannot do without. A refusal
      // here is the screen's refusal.
      setSpend(await get("/cloud-spend", { limit: 200 }));
      setFailure(null);
    } catch (e) {
      setFailure(e);
      setSpend(null);
      return;
    }
    // The agent half is best effort. An org with no agents is the ordinary
    // case, and a console that refused to show the bill because nothing was
    // running agents would be failing at what somebody came for.
    get("/agents")
      .then((b) => setAgents(b?.agents || []))
      .catch(() => setAgents([]));
  }, [get]);

  useEffect(() => {
    load();
    onEffective?.(null);
  }, [load, onEffective]);

  const model = useMemo(() => {
    const items = spend?.items || [];
    const months = items.filter((i) => i.fields?.grain === "month");

    // The month in progress, **for the bill this screen is about**.
    //
    // This used to be "the latest month row there is", which was true while
    // there was one row per month. Adding the operator's own costs made two,
    // and the sort broke the tie arbitrarily: the screen took its `as_of`,
    // its projection and its basis from whichever came first. It picked the
    // operator's, so a headline of $44.04 sat above "where the month lands:
    // $590.71" — the cost of running the platform, presented as this org's
    // forecast. Thirteen times out and contradictory on its face.
    //
    // Filtered by meaning rather than made deterministic. A stable sort would
    // have picked one consistently and been just as wrong; the fix is that
    // these figures are about money this org was charged, and an operator row
    // is not that.
    const billed = months.filter((i) => i.fields?.attribution !== "operator");
    const current = billed
      .slice()
      .sort((a, b) => (a.fields.period_start < b.fields.period_start ? 1 : -1))[0];

    // The operator's own month row, kept separately for the block that says
    // what running this costs.
    const operatorMonth = months
      .filter((i) => i.fields?.attribution === "operator")
      .sort((a, b) => (a.fields.period_start < b.fields.period_start ? 1 : -1))[0];

    const period = current?.fields?.period_start?.slice(0, 7) || "";
    const monthRows = items.filter(
      (i) => i.fields?.grain === "month" && i.fields.period_start?.slice(0, 7) === period,
    );

    // Cloud, per app, from the month rows of the month in progress.
    const cloud = new Map();
    let unattributed = 0;
    // What it costs to run the platform, which is not money anybody here was
    // charged. Kept out of every total below for that reason \u2014 adding the
    // operator's costs to a customer's bill is the one sum nobody wants.
    let operator = 0;
    // What sandboxes cost (LO-190). Real money in this account, so it stays in
    // the bill, and not an app: a sandbox is a test of a request that is not
    // installed, so it is one line that names no instance and no app, and it
    // is never counted among the apps above it.
    let sandboxes = 0;
    for (const row of monthRows) {
      const f = row.fields || {};
      const amount = fromMinor(f.amount);
      if (f.attribution === "operator") {
        operator += amount;
      } else if (f.attribution === "instance" && f.app === SANDBOX_APP) {
        sandboxes += amount;
      } else if (f.attribution === "instance" && f.app) {
        cloud.set(f.app, (cloud.get(f.app) || 0) + amount);
      } else {
        unattributed += amount;
      }
    }

    // Agents, per app type, for the same month as the cloud figures above.
    //
    // From `runs_series`, where each run carries its own `at`, rather than
    // from `spend.usd`, which accumulates over every run ever and has no
    // date on it.
    //
    // Undated spend is kept **per app** and not as one total at the foot of
    // the page. The first version summed it, which dropped two apps out of
    // the table entirely: `ledger-review` has spent $19.14 and every run of
    // it predates per-run rows, so its month figure is zero and its row
    // vanished. An app that costs money disappearing from the costs table is
    // a worse answer than the one it replaced.
    const from = `${period}-01`;
    const byApp = new Map();
    for (const a of agents) {
      const runs = a?.runs_series || [];
      const dated = runs.reduce((n, r) => n + (Number(r.usd) || 0), 0);
      const thisMonth = runs
        .filter((r) => String(r.at || "") >= from)
        .reduce((n, r) => n + (Number(r.usd) || 0), 0);
      const undated = Math.max(0, Number(a?.spend?.usd || 0) - dated);

      if (!thisMonth && !undated) continue;
      const found = byApp.get(a.app_type) || { usd: 0, undated: 0, names: [] };
      found.usd += thisMonth;
      found.undated += undated;
      found.names.push(a.name);
      byApp.set(a.app_type, found);
    }

    const apps = new Set([...cloud.keys(), ...byApp.keys()]);
    const rows = [...apps].map((app) => ({
      key: app,
      label: app,
      cloud: cloud.get(app) || 0,
      agents: byApp.get(app)?.usd || 0,
      undatedAgents: byApp.get(app)?.undated || 0,
      agentNames: byApp.get(app)?.names || [],
      unattributed: false,
    }));
    // Untagged money, split by whether the service is the platform's or an
    // app's. The second is a gap; the first is just what Kernel costs.
    let platform = 0;
    let untraced = 0;
    for (const row of monthRows) {
      const f = row.fields || {};
      if (f.attribution === "instance" && f.app) continue;
      if (f.attribution === "operator") continue;
      for (const item of f.by_service || []) {
        const amount = fromMinor(item.amount);
        if (!amount) continue;
        if (isPlatform(item.service)) platform += amount;
        else untraced += amount;
      }
    }

    if (platform) {
      rows.push({
        key: "__platform__",
        // Was "Kernel platform", and that label is why a real gap went
        // unnoticed for weeks: it reads as *what the platform costs to run*
        // and means *untagged services inside this account*. Two different
        // numbers, and the one people wanted was the other one.
        label: "Shared services here",
        cloud: platform,
        agents: 0,
        agentNames: [],
        platform: true,
      });
    }
    if (untraced) {
      rows.push({
        key: "__untraced__",
        label: "An app's, but not yet which one",
        cloud: untraced,
        agents: 0,
        agentNames: [],
        untraced: true,
      });
    }
    if (sandboxes) {
      rows.push({
        key: "__sandboxes__",
        label: "Sandboxes, for requests not yet approved",
        cloud: sandboxes,
        agents: 0,
        agentNames: [],
        sandbox: true,
      });
    }
    rows.sort((a, b) => (b.cloud + b.agents) - (a.cloud + a.agents));

    const cloudTotal = [...cloud.values()].reduce((n, v) => n + v, 0) + unattributed + sandboxes;
    const agentTotal = [...byApp.values()].reduce((n, v) => n + v.usd, 0);

    return {
      period,
      rows,
      cloudTotal,
      agentTotal,
      unattributed,
      sandboxes,
      operator,
      operatorProjected: operatorMonth?.fields?.projected_amount
        ? fromMinor(operatorMonth.fields.projected_amount)
        : null,
      operatorBasis: operatorMonth?.fields?.projection_basis || "",
      asOf: current?.fields?.as_of || "",
      projected: current?.fields?.projected_amount
        ? fromMinor(current.fields.projected_amount)
        : null,
      basis: current?.fields?.projection_basis || "",
      daysObserved: current?.fields?.days_observed,
      platform,
      untraced,
      undated: [...byApp.values()].reduce((n, v) => n + v.undated, 0),
      services: (() => {
        // What the money that is not against an app was actually spent on.
        // Summed across the month rows that carry no app, biggest first, and
        // zero-cost services dropped — a list with seven zeroes in it reads
        // as noise and hides the one line that matters.
        const totals = new Map();
        for (const row of monthRows) {
          const f = row.fields || {};
          if (f.attribution === "instance" && f.app) continue;
          for (const item of f.by_service || []) {
            const amount = fromMinor(item.amount);
            if (!amount || !isPlatform(item.service)) continue;
            totals.set(item.service, (totals.get(item.service) || 0) + amount);
          }
        }
        return [...totals.entries()]
          .map(([service, usd]) => ({ service, usd }))
          .sort((a, b) => b.usd - a.usd);
      })(),
      widest: rows.length ? Math.max(...rows.map((r) => r.cloud + r.agents)) : 0,
      any: items.length > 0,
    };
  }, [spend, agents]);

  if (failure) {
    return <Refusal failure={failure} actor="This console" onRetry={load} />;
  }
  if (!spend) return <Loading label="Reading what this is costing" />;

  // Usage: what the apps and their agents actually did this month.
  const usage = model.cloudTotal + model.agentTotal;
  // Everything, including the platform they all run on.
  //
  // **The headline is the whole bill, and that is a correction.** It used to
  // be `usage` alone, with the platform underneath in a paragraph. On this
  // kernel that meant $44.04 in the large figure and $590.71 in small print
  // — a reader takes the number they were shown and is surprised by the one
  // they were not. The rule this now follows: *nothing in small print may be
  // larger than what is in large print.*
  //
  // Summed here and split immediately below, which is the honest shape. The
  // two halves behave completely differently — the platform is a fixed cost
  // that exists before any app does, usage is marginal and today it is
  // pennies — so a reader needs both the total and the split, in that order.
  const combined = usage + model.operator;
  const projectedAll = (model.projected || 0) + (model.operatorProjected || 0);

  return (
    <Stack spacing={4} sx={{ minWidth: 0 }}>
      <Headline
        eyebrow={model.period
          ? `What this costs to run, ${model.period}`
          : "What this costs to run"}
        figure={money(combined)}
        // Both halves are this calendar month, which is what the basis three
        // lines below already says.
        //
        // This comment used to say the agent half was "every run ever",
        // left over from before agent spend was scoped to the period. A
        // comment contradicting the copy beside it is worse than no comment:
        // the next reader has to work out which one lied, and on a screen
        // whose whole argument is that its figures are qualified honestly,
        // that is the sentence that costs the rest of them their credit.
        caption="this month, everything"
        // Attention only when something needs doing. Platform spend is not
        // a problem to be fixed, it is what the thing costs.
        tone={model.untraced ? "attention" : undefined}
        lede={
          !model.any
            ? "Nothing has been reported yet. The daily job writes one record per account per day; until it has run there is nothing here to be wrong."
            : model.operator
              // Biggest part first, and named. Somebody reading one line of
              // this screen should come away knowing which number is the one
              // that will surprise them.
              ? `${money(model.operator)} of it is the kernel itself \u2014 the shared platform every app runs on, there before any app is. ${money(usage)} is usage: what the apps and their agents actually did.`
              : model.platform
                ? `${money(model.platform)} of it is shared services in this account rather than any one app`
                : model.sandboxes
                  ? "Every dollar here belongs to an app, or to a sandbox for a request that has not been approved."
                  : "Every dollar here belongs to an app."
        }
        basis={
          "Three different kinds of number. The kernel is the platform every "
          + "app runs on — a fixed cost that exists before any app does and "
          + "does not fall when usage does. Cloud is what AWS charged for the "
          + "apps themselves — a bill. Agents is computed from each "
          + "session's reported usage at list prices, so an org on "
          + "committed-use pricing paid less than this says and nothing here "
          + "knows by how much. All three are this calendar month."
        }
      />

      {model.operator ? (
        <Box>
          <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
            What it costs to run this
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: "62ch" }}>
            <strong>{money(model.operator)}</strong> this month, in the
            operator's own AWS account. Not part of any figure above and
            not money anybody here was charged — every other number on this
            screen is a bill somebody receives, and this is the cost of
            serving them.
          </Typography>
          {model.operatorProjected ? (
            <Typography variant="body2" color="text.secondary"
                        sx={{ mb: 1, maxWidth: "62ch" }}>
              On track for <strong>{money(model.operatorProjected)}</strong> by
              the end of the month, at the rate of the last few days. That is
              the figure to weigh against what this is worth, and it is not
              the projection above — that one is this org's own cloud bill.
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: "block", maxWidth: "62ch" }}>
            Almost all of it is the Crossplane control plane — an EKS cluster
            and its nodes, running whether or not anybody provisions
            anything. The kernel's own services are cents at this volume.
            Who should own and pay for that cluster is undecided (LO-89), and
            this figure is the number that argument was missing.
          </Typography>
        </Box>
      ) : null}

      {model.services.length ? (
        <Box>
          <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
            Shared services in this account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: "62ch" }}>
            Search, keys, secrets and DNS: what Kernel needs running in
            <em> this</em> account before any app exists. One set for the whole
            org, so it is not spend waiting to be assigned to an app — it is
            spend that does not belong to one. Distinct from what it costs to
            run the platform itself, which is the figure above and is in a
            different account.
          </Typography>
          <Stack spacing={0.5} sx={{ maxWidth: "34rem" }}>
            {model.services.map((row) => (
              <Stack
                key={row.service}
                direction="row"
                spacing={2}
                sx={{ alignItems: "baseline", justifyContent: "space-between" }}
              >
                <Typography variant="body2" sx={{ minWidth: 0 }}>
                  {row.service}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
                >
                  {money(row.usd)}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, maxWidth: "62ch" }}>
            {/* Computed, not asserted. This read "the per-app services are
                at zero this month" while an untraced row on the same screen
                showed $0.10 of them — a sentence that was true of the three
                services it happened to name and false of the screen it sat
                on. A claim about the numbers belongs in the numbers. */}
            {model.untraced
              ? `${money(model.untraced)} is a per-app service and not the platform's. `
              : "The per-app services are at zero this month. "}
            The apps themselves are close to free; the platform underneath
            them is the bill.
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ minWidth: 0, overflowX: "auto" }}>
        <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
          By app
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, maxWidth: "62ch" }}>
          What each app costs to run, and what its agents cost to think. An app
          with no agents shows a dash rather than a zero — it has none, which
          is different from having some that cost nothing.
        </Typography>
        {model.rows.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>App</TableCell>
                <TableCell align="right">Cloud</TableCell>
                <TableCell align="right">Agents</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {model.rows.map((row) => (
                <AppRow key={row.key} row={row} widest={model.widest} />
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nothing to show. No spend has been reported and no agent has run.
          </Typography>
        )}
      </Box>

      {model.projected !== null ? (
        <Box>
          <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
            Where the month lands
          </Typography>
          <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
            <Typography variant="h2" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {money(projectedAll)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              cloud, projected
            </Typography>
          </Stack>
          {/* Split immediately, largest first, for the same reason the
              headline is. A single projected figure with the platform folded
              invisibly into it would be the old mistake wearing a bigger
              number. */}
          {model.operatorProjected ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: "62ch" }}>
              <strong>{money(model.operatorProjected)}</strong> of that is the
              kernel, which is fixed and lands there whether or not anybody
              uses it. <strong>{money(model.projected || 0)}</strong> is this
              org's own cloud, which moves with what the apps do.
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: "62ch" }}>
            {BASIS_NOTE[model.basis] || model.basis}
            {model.basis === "run_rate_7d" && model.daysObserved
              ? ` ${model.daysObserved} complete ${model.daysObserved === 1 ? "day" : "days"} observed.`
              : ""}{" "}
            Agent spend is not projected: an agent runs when something schedules
            it, so last week's rate says nothing about next week's. Cloud
            figures are complete to{" "}
            <code>{model.asOf ? model.asOf.slice(0, 10) : "unknown"}</code>,
            which is as far as Cost Explorer had data when this was written.
          </Typography>
        </Box>
      ) : null}

    </Stack>
  );
}
